import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { usePublicSiteSettings } from "@/hooks/useSiteSettings";

interface PageSEOProps {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
}

/**
 * Dynamically injects <title>, meta description, OG tags, and canonical into <head>.
 * Reads overrides from site_settings (keys: seo_{route}_{field}).
 * Falls back to props, then to global defaults.
 */
export function PageSEO({
  title: propTitle,
  description: propDesc,
  ogTitle: propOgTitle,
  ogDescription: propOgDesc,
  ogImage: propOgImage,
  canonical: propCanonical,
  noindex = false,
}: PageSEOProps) {
  const location = useLocation();
  const { data: settings } = usePublicSiteSettings();

  useEffect(() => {
    if (!settings) return;

    // Derive route key: "/" → "home", "/outsourcing" → "outsourcing", "/pricing" → "pricing"
    const routeKey = location.pathname === "/" ? "home" : location.pathname.replace(/^\//, "").replace(/\//g, "_");
    const prefix = `seo_${routeKey}_`;

    const siteName = settings["seo_site_name"] || "NeoSDR";

    const title = settings[`${prefix}title`] || propTitle || siteName;
    const description = settings[`${prefix}description`] || propDesc || "";
    const ogTitle = settings[`${prefix}og_title`] || propOgTitle || title;
    const ogDescription = settings[`${prefix}og_description`] || propOgDesc || description;
    const ogImage = settings[`${prefix}og_image`] || propOgImage || settings["seo_default_og_image"] || "";
    const canonical = settings[`${prefix}canonical`] || propCanonical || window.location.href;

    // Title
    document.title = title;

    // Helper to set/create a meta tag
    const setMeta = (name: string, content: string, property = false) => {
      if (!content) return;
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta("description", description);
    setMeta("og:title", ogTitle, true);
    setMeta("og:description", ogDescription, true);
    setMeta("og:image", ogImage, true);
    setMeta("og:url", canonical, true);
    setMeta("og:type", "website", true);
    setMeta("og:site_name", siteName, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", ogTitle);
    setMeta("twitter:description", ogDescription);
    setMeta("twitter:image", ogImage);

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;

    // Noindex
    if (noindex) {
      setMeta("robots", "noindex, nofollow");
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    }
  }, [settings, location.pathname, propTitle, propDesc, propOgTitle, propOgDesc, propOgImage, propCanonical, noindex]);

  return null;
}
